import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useSWR from 'swr';
import Pagination from 'components/Pagination/Pagination';
import { User } from 'views/types/typeUser';
import { useDebounceValue } from 'usehooks-ts';
import { useWalletsFilterStore } from 'views/store/useUserWalletsTableStore';
import { TWallet } from 'views/types/wallets';
import { walletsService } from 'views/services/walletService';
import { toast } from 'App';
import { CopyButton } from 'components/button/CopyButton';

interface Paginated<T> {
  data: T[];
  meta: {
    totalItems: number;
    totalPages: number;
  };
}

interface IProps {
  user: User;
}

const columnHelper = createColumnHelper<TWallet>();

const getColumns = () => [
  columnHelper.accessor('address', {
    header: 'Address',
    cell: info => {
      const address = info.getValue();

      const copyToClipboard = () => {
        toast({
          title: 'Address copied to clipboard',
          description: '',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      };

      return (
        <div className="flex items-center gap-2">
          <span className="font-medium font-mono text-sm">{address}</span>

          <CopyButton
            className="h-4 w-4"
            value={address}
            cb={copyToClipboard}
          />
        </div>
      );
    },
  }),
  columnHelper.accessor('network_type', {
    header: 'Network Type',
    cell: info => (
      <span className="font-semibold font-mono text-sm">{info.getValue()}</span>
    ),
  }),
];

export const UserWallets = ({ user }: IProps) => {
  const {
    page,
    setPage,
    networkType,
    searchBy,
    itemsPerPage,
    setItemsPerPage,
    sortState,
  } = useWalletsFilterStore();

  const [searchDebounce] = useDebounceValue(searchBy, 500);

  const { data, isLoading } = useSWR<Paginated<TWallet>>(
    [
      '/api/wallets',
      {
        page: page + 1,
        limit: itemsPerPage,
        search: user.id,
        searchBy: searchDebounce ? searchDebounce : 'user_id',
        networkType: networkType || undefined,
        ...(sortState.sortBy && { sortBy: sortState.sortBy }),
        ...(sortState.sortOrder && { sortOrder: sortState.sortOrder }),
      },
    ],
    ([_, params]) => walletsService.getWallets(params)
  );

  const table = useReactTable({
    data: data?.data || [],
    columns: getColumns(),
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  return (
    <>
      {isLoading ? (
        <span>Loading...</span>
      ) : !data || data.data.length === 0 ? (
        <div className="text-center py-8">
          <div className="font-semibold text-gray-500">No wallets found</div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="bg-white pb-2 px-4 pt-4 text-start dark:bg-navy-800"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors duration-200 [&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900"
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="p-2 px-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={data?.meta?.totalPages}
            totalItems={data?.meta?.totalItems}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            isBottomIndicators
          />
        </>
      )}
    </>
  );
};
